/**
 * Cloud Backup Service — syncs localStorage-only data to Supabase for paid users.
 *
 * 7 data types backed up: chat_histories, diy_guides, aa_analyses, aa_checklist,
 * mileage_logs, dismissed_items, user_preferences.
 *
 * Photos in chat messages are stripped (metadata only) to keep backup size small.
 * The Export/Download feature includes full photos since it goes to the user's machine.
 */
import { supabase } from '../config/supabase';
import { reliableWrite } from './writeQueue';

const DEV = import.meta.env.DEV;

/* ─── Constants ─── */

const DEBOUNCE_MS = 2000;
const debounceTimers = {};

const LS_KEY_TO_DATA_TYPE = {
  pm_chat_histories: 'chat_histories',
  pm_diy_guides: 'diy_guides',
  pm_aa_analyses: 'aa_analyses',
  pm_aa_checklist: 'aa_checklist',
  pm_mileage_logs: 'mileage_logs',
  pm_dismissed_items: 'dismissed_items',
  pm_settings: 'user_preferences',
};

/* ─── Paid user check (reads localStorage directly to avoid circular import) ─── */

export function isPaidUser() {
  try {
    const raw = localStorage.getItem('pm_settings');
    if (!raw) return false;
    const settings = JSON.parse(raw);
    return settings?.subscription?.plan !== 'free';
  } catch {
    return false;
  }
}

/* ─── Photo stripping for cloud backup ─── */

function stripPhotosFromChat(chatData) {
  if (!chatData || typeof chatData !== 'object') return chatData;
  const stripped = {};
  for (const [vehicleId, messages] of Object.entries(chatData)) {
    if (!Array.isArray(messages)) {
      stripped[vehicleId] = messages;
      continue;
    }
    stripped[vehicleId] = messages.map((msg) => {
      if (!msg.photos || msg.photos.length === 0) return msg;
      return {
        ...msg,
        photos: msg.photos.map((p) => ({
          id: p.id,
          label: p.label || null,
          sizeBytes: p.dataUrl ? p.dataUrl.length : 0,
          // dataUrl intentionally omitted for cloud backup
        })),
      };
    });
  }
  return stripped;
}

/* ─── Extract preferences only (not subscription) from pm_settings ─── */

function extractPreferences(raw) {
  try {
    const settings = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return settings?.preferences || {};
  } catch {
    return {};
  }
}

/* ─── Build backup payload from a localStorage key ─── */

function getBackupPayload(lsKey) {
  const dataType = LS_KEY_TO_DATA_TYPE[lsKey];
  if (!dataType) return null;

  const raw = localStorage.getItem(lsKey);
  if (!raw) return null;

  let data;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }

  // Strip photos from chat histories
  if (dataType === 'chat_histories') {
    data = stripPhotosFromChat(data);
  }

  // Extract only preferences from settings
  if (dataType === 'user_preferences') {
    data = extractPreferences(data);
  }

  return { dataType, data };
}

/* ─── Supabase RPC: upsert a single data type ─── */

export async function syncBackupDataType(userId, dataType, data) {
  const jsonStr = JSON.stringify(data);
  const sizeBytes = new Blob([jsonStr]).size;

  const { error } = await supabase.rpc('upsert_user_backup', {
    b_user_id: userId,
    b_data_type: dataType,
    b_data: data,
    b_data_size_bytes: sizeBytes,
  });

  if (error) throw error;
  if (DEV) console.log('[Backup] Synced', dataType, `(${sizeBytes} bytes)`);
}

/* ─── Debounced sync trigger (called from storage.js on every save) ─── */

export function triggerBackupSync(lsKey, userId) {
  if (!isPaidUser() || !userId) return;

  const payload = getBackupPayload(lsKey);
  if (!payload) return;

  // Clear existing debounce timer for this data type
  if (debounceTimers[payload.dataType]) {
    clearTimeout(debounceTimers[payload.dataType]);
  }

  debounceTimers[payload.dataType] = setTimeout(() => {
    delete debounceTimers[payload.dataType];

    const { dataType, data } = payload;
    reliableWrite(
      'backup',
      { userId, dataType, data },
      () => syncBackupDataType(userId, dataType, data),
    );
  }, DEBOUNCE_MS);
}

/* ─── Load all backup data from Supabase (for hydration on login) ─── */

export async function loadAllBackupData(userId) {
  const { data, error } = await supabase.rpc('load_user_backup', {
    target_user_id: userId,
  });
  if (error) throw error;
  return data || [];
}

/* ─── Full sync: push all 7 data types to Supabase ─── */

export async function syncAllBackupData(userId) {
  if (!isPaidUser() || !userId) return { synced: 0, errors: 0 };

  const localKeys = Object.keys(LS_KEY_TO_DATA_TYPE);
  let synced = 0;
  let errors = 0;

  for (const key of localKeys) {
    const payload = getBackupPayload(key);
    if (!payload) continue;

    try {
      await syncBackupDataType(userId, payload.dataType, payload.data);
      synced++;
    } catch (err) {
      if (DEV) console.error('[Backup] Full sync failed for', payload.dataType, err.message);
      errors++;
    }
  }

  if (DEV) console.log('[Backup] Full sync complete:', { synced, errors });
  return { synced, errors };
}

/* ─── Export: build full data object for download ─── */

export function buildExportData() {
  const EXPORT_KEYS = {
    pm_garages: 'garages',
    pm_vehicles: 'vehicles',
    pm_analyses: 'analyses',
    pm_service_history: 'serviceHistory',
    pm_chat_histories: 'chatHistories',
    pm_aa_analyses: 'autoAdvisorAnalyses',
    pm_aa_checklist: 'autoAdvisorChecklists',
    pm_value_history: 'valueHistory',
    pm_mileage_logs: 'mileageLogs',
    pm_dismissed_items: 'dismissedItems',
    pm_diy_guides: 'diyGuides',
    pm_reminders: 'reminders',
    pm_settings: 'settings',
    pm_user_profile: 'userProfile',
  };

  const exportData = {
    exportedAt: new Date().toISOString(),
    format: 'pocket-mechanic-export-v1',
  };

  for (const [lsKey, exportKey] of Object.entries(EXPORT_KEYS)) {
    const raw = localStorage.getItem(lsKey);
    try {
      exportData[exportKey] = raw ? JSON.parse(raw) : null;
    } catch {
      exportData[exportKey] = null;
    }
  }

  return exportData;
}

/* ─── Download export as JSON file ─── */

export function downloadExportAsJson() {
  const data = buildExportData();
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = `pocket-mechanic-export-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  return { sizeBytes: blob.size };
}
